import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import dayjs from "dayjs";
import { getToken } from "@/utils/auth";
import ProtectedRoute from "@/components/ProtectedRoute";
import RoleGate from "@/components/RoleGate";
import Button from "@/components/Base/Button";
import Lucide from "@/components/Base/Lucide";
import axios from "axios";

const PRODUCT_TYPE_ORDER = ["Devices", "Refills", "Accessories"];

type ReceiptLine = {
  id: string;
  productId: string;
  quantity: number;
  product: { id: string; name: string; sku?: string | null; type?: string | null; productImageUrl?: string | null };
};

type Receipt = {
  id: string;
  batchNo: string;
  receivedAt: string;
  refNo: string | null;
  documentUrl: string | null;
  lines: ReceiptLine[];
};

export default function ViewStockInPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const companyIdFromUrl = searchParams.get("companyId");

  const [receipt, setReceipt] = useState<Receipt | null>(null);
  const [productBalances, setProductBalances] = useState<Record<string, number>>({});
  const [loading, setLoading] = useState(true);
  const [expandedTypeIds, setExpandedTypeIds] = useState<Set<string>>(new Set());

  const apiUrl = import.meta.env.VITE_API_URL || "http://localhost:4000";
  const token = getToken();
  const queryCompany = companyIdFromUrl ? `?companyId=${companyIdFromUrl}` : "";

  const linesByType = useMemo(() => {
    if (!receipt?.lines?.length) return [];
    const map = new Map<string, ReceiptLine[]>();
    for (const line of receipt.lines) {
      const typeLabel = (line.product?.type || "").trim() || "Other";
      if (!map.has(typeLabel)) map.set(typeLabel, []);
      map.get(typeLabel)!.push(line);
    }
    const ordered: [string, ReceiptLine[]][] = [];
    for (const name of PRODUCT_TYPE_ORDER) {
      const found = Array.from(map.entries()).find(([k]) => k.trim().toLowerCase() === name.toLowerCase());
      if (found) ordered.push(found);
    }
    for (const [label, lines] of map.entries()) {
      if (!PRODUCT_TYPE_ORDER.some((n) => n.toLowerCase() === label.trim().toLowerCase())) ordered.push([label, lines]);
    }
    return ordered;
  }, [receipt?.lines]);

  useEffect(() => {
    if (!receipt?.lines?.length) return;
    const typeLabels = linesByType.map(([label]) => label);
    setExpandedTypeIds((prev) => {
      const next = new Set(prev);
      typeLabels.forEach((l) => next.add(l));
      return next;
    });
  }, [receipt?.id, receipt?.lines?.length, linesByType]);

  useEffect(() => {
    if (!id) return;
    (async () => {
      try {
        setLoading(true);
        const res = await axios.get(`${apiUrl}/stock-in/${id}${queryCompany}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setReceipt(res.data as Receipt);
      } catch (err) {
        console.error("Failed to load Stock IN receipt", err);
        setReceipt(null);
      } finally {
        setLoading(false);
      }
    })();
  }, [id, apiUrl, queryCompany, token]);

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${apiUrl}/stock-out/product-balances${queryCompany}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (Array.isArray(res.data)) {
          const map: Record<string, number> = {};
          res.data.forEach((row: { productId: string; balance: number }) => {
            map[row.productId] = row.balance;
          });
          setProductBalances(map);
        }
      } catch (err) {
        console.error("Failed to load product balances", err);
      }
    })();
  }, [apiUrl, queryCompany, token]);

  const toggleType = useCallback((typeLabel: string) => {
    setExpandedTypeIds((prev) => {
      const next = new Set(prev);
      if (next.has(typeLabel)) next.delete(typeLabel);
      else next.add(typeLabel);
      return next;
    });
  }, []);

  const handleBack = () => {
    navigate(companyIdFromUrl ? `/stock-in?companyId=${companyIdFromUrl}` : "/stock-in");
  };

  const editUrl = id
    ? companyIdFromUrl
      ? `/stock-in/${id}/edit?companyId=${companyIdFromUrl}`
      : `/stock-in/${id}/edit`
    : "/stock-in";

  if (loading) {
    return (
      <ProtectedRoute>
        <RoleGate allow={["SUPERADMIN", "ADMIN"]}>
          <div className="flex items-center justify-center py-20">
            <Lucide icon="Loader2" className="w-8 h-8 animate-spin text-slate-500" />
          </div>
        </RoleGate>
      </ProtectedRoute>
    );
  }

  if (!receipt) {
    return (
      <ProtectedRoute>
        <RoleGate allow={["SUPERADMIN", "ADMIN"]}>
          <div className="p-5">
            <div className="text-center text-slate-500 mb-4">Stock IN record not found.</div>
            <Button variant="outline-secondary" onClick={handleBack}>
              Back to list
            </Button>
          </div>
        </RoleGate>
      </ProtectedRoute>
    );
  }

  return (
    <ProtectedRoute>
      <RoleGate allow={["SUPERADMIN", "ADMIN"]}>
        <div className="grid grid-cols-12 gap-y-10 gap-x-6 w-full">
          <div className="col-span-12">
            <div className="flex flex-col mt-4 md:mt-0 md:h-10 gap-y-3 md:items-center md:flex-row">
              <div className="text-base font-medium group-[.mode--light]:text-white">Stock IN details</div>
              <div className="flex flex-col sm:flex-row gap-x-3 gap-y-2 md:ml-auto">
                <Button variant="outline-secondary" onClick={handleBack}>
                  <Lucide icon="ArrowLeft" className="stroke-[1.3] w-4 h-4 mr-2" />
                  Back to list
                </Button>
                <Button
                  variant="primary"
                  className="!bg-[#2D2926] hover:!bg-[#2D2926]/90"
                  onClick={() => navigate(editUrl)}
                >
                  <Lucide icon="Pencil" className="stroke-[1.3] w-4 h-4 mr-2" />
                  Edit
                </Button>
              </div>
            </div>

            <div className="mt-3.5 flex flex-col gap-6">
              <div className="box p-5">
                <div className="text-[0.94rem] font-medium pb-4 border-b border-slate-200/60 dark:border-darkmode-400">
                  Delivery Order details
                </div>
                <div className="mt-5 flex flex-wrap items-center gap-6">
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-slate-500 shrink-0">Batch No</span>
                    <span className="text-sm font-medium">{receipt.batchNo}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-slate-500 shrink-0">Date</span>
                    <span className="text-sm">
                      {receipt.receivedAt ? dayjs(receipt.receivedAt).format("YYYY-MM-DD") : "—"}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-slate-500 shrink-0">Ref No</span>
                    <span className="text-sm">{receipt.refNo || "—"}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-slate-500 shrink-0">Document</span>
                    {receipt.documentUrl ? (
                      <a
                        href={receipt.documentUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-sm text-primary hover:underline"
                      >
                        View document
                      </a>
                    ) : (
                      <span className="text-sm">—</span>
                    )}
                  </div>
                </div>
              </div>

              {linesByType.length === 0 ? (
                <div className="box p-10 text-center text-slate-500">No product lines.</div>
              ) : (
                linesByType.map(([typeLabel, lines]) => {
                  const isExpanded = expandedTypeIds.has(typeLabel);
                  return (
                    <div key={typeLabel} className="box overflow-hidden">
                      <button
                        type="button"
                        className="flex items-center gap-3 w-full text-left p-5 hover:bg-slate-50/50 dark:hover:bg-darkmode-400/30 transition-colors"
                        onClick={() => toggleType(typeLabel)}
                        aria-expanded={isExpanded}
                      >
                        <div className="text-[0.94rem] font-medium">{typeLabel}</div>
                        <Lucide
                          icon={isExpanded ? "ChevronUp" : "ChevronDown"}
                          className="w-5 h-5 stroke-[1.3] ml-auto text-slate-500 shrink-0"
                        />
                      </button>
                      {isExpanded && (
                        <div className="px-12 pb-6 pt-0 border-t border-slate-200/60 dark:border-darkmode-400">
                          <div className="grid grid-cols-[5fr_2fr] gap-4 items-center py-2 border-b border-slate-200/40 dark:border-darkmode-400/60 text-xs font-medium text-slate-500">
                            <div>Product</div>
                            <div className="text-center">IN</div>
                          </div>
                          {lines.map((line) => (
                            <div
                              key={line.id}
                              className="grid grid-cols-[5fr_2fr] gap-4 items-center py-2 border-b border-slate-200/40 dark:border-darkmode-400/60 last:border-b-0"
                            >
                              <div className="flex items-center gap-3 min-w-0">
                                <div className="w-12 h-12 shrink-0 rounded overflow-hidden bg-slate-100 dark:bg-darkmode-700 flex items-center justify-center">
                                  {line.product?.productImageUrl ? (
                                    <img
                                      src={line.product.productImageUrl}
                                      alt=""
                                      className="w-full h-full object-contain"
                                    />
                                  ) : (
                                    <Lucide icon="Package" className="w-6 h-6 text-slate-400" />
                                  )}
                                </div>
                                <div className="min-w-0">
                                  <div className="text-sm font-medium truncate">{line.product?.name ?? "—"}</div>
                                  {line.product?.sku && (
                                    <div className="text-xs text-slate-500">{line.product.sku}</div>
                                  )}
                                </div>
                              </div>
                              <div className="flex justify-center items-center text-sm font-medium tabular-nums">
                                {line.quantity}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })
              )}
            </div>
          </div>
        </div>
      </RoleGate>
    </ProtectedRoute>
  );
}
